import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { AlertController, IonButton, IonIcon, IonicSafeString, LoadingController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicScrollManagerService } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IUser } from '@okcargo-apps/shared-domain';
import { UsersMutationsService } from '../users-mutations.service';
import { USERS_LIST_MESSAGE_OBJECT_ID } from '../users-store.model';
import { UsersStoreService } from '../users-store.service';

@Component({
  selector: 'lib-user-delete-action',
  templateUrl: './user-delete-action.component.html',
  styleUrls: ['./user-delete-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon, TranslateModule],
})
export class UserDeleteActionComponent {
  public readonly user = input.required<IUser>();
  public readonly canRemove = input<boolean>();

  private readonly alertCtrl = inject(AlertController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly usersStoreService = inject(UsersStoreService);
  private readonly usersMutationsService = inject(UsersMutationsService);

  public async confirmDelete(): Promise<void> {
    const user = this.user();
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('general.attention'),
      message: new IonicSafeString(
        this.translate.instant('users.are_you_sure_delete', { name: `${user.firstName} ${user.lastName}` }),
      ),
      buttons: [
        { text: this.translate.instant('general.cancel'), role: 'cancel' },
        {
          text: this.translate.instant('general.confirm_delete'),
          role: 'destructive',
          handler: () => this.deleteUser(user),
        },
      ],
    });

    await alert.present();
  }

  private async deleteUser(user: IUser): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    try {
      await this.usersMutationsService.softDeleteUser(user.id);

      this.messagesUiService.setMessage({
        objectId: USERS_LIST_MESSAGE_OBJECT_ID,
        message: [
          this.translate.instant('users.deleted_message', { name: `${user.firstName} ${user.lastName}`.trim() }),
        ],
      });

      loading.message = this.translate.instant('general.loading_data');
      await this.usersStoreService.reloadAfterSaveData();
    } catch (error) {
      console.warn(error);
    } finally {
      await loading.dismiss();
    }

    setTimeout(() => this.ionicScrollManagerService.scrollToTop(), 300);
  }
}
